import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  QrCode,
  Store,
  Check,
  Copy,
  Download,
  Share2,
  Settings,
  DollarSign,
  Clock,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { QRCodeSVG } from "qrcode.react";

const MerchantQR = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchantData, setMerchantData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [requestAmount, setRequestAmount] = useState("");
  const [showAmountQR, setShowAmountQR] = useState(false);

  useEffect(() => {
    fetchMerchantData();
  }, []);

  const fetchMerchantData = async () => {
    try {
      const token = localStorage.getItem("stelx_token");
      
      // For now, create merchant QR data from user info
      // In production, this would be a dedicated merchant endpoint
      const userResponse = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = userResponse.data;
      
      setMerchantData({
        merchant_id: `merchant_${userData.user_id}`,
        business_name: userData.name || "My Business",
        qr_code: `stelxpay://pay/${userData.user_id}`,
        total_received: 0, // Would come from backend
        transactions_today: 0,
        average_transaction: 0
      });
    } catch (error) {
      console.error("Error fetching merchant data:", error);
      // Create mock data for demo
      setMerchantData({
        merchant_id: `merchant_${user?.user_id || 'demo'}`,
        business_name: user?.name || "My Business",
        qr_code: `stelxpay://pay/${user?.user_id || 'demo'}`,
        total_received: 125750,
        transactions_today: 12,
        average_transaction: 10479
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePaymentQR = () => {
    if (!requestAmount || parseFloat(requestAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setShowAmountQR(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareQR = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pay ${merchantData?.business_name}`,
          text: `Scan to pay ${merchantData?.business_name} via Stelx Pay`,
          url: `https://pay.stelxpay.com/m/${merchantData?.merchant_id}`
        });
      } catch (error) {
        copyToClipboard(`https://pay.stelxpay.com/m/${merchantData?.merchant_id}`);
      }
    } else {
      copyToClipboard(`https://pay.stelxpay.com/m/${merchantData?.merchant_id}`);
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById("merchant-qr-code");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `stelx-merchant-qr-${merchantData?.merchant_id}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    toast.success("QR code downloaded!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const qrValue = showAmountQR && requestAmount
    ? `stelxpay://pay/${user?.user_id}?amount=${requestAmount}&currency=JMD`
    : merchantData?.qr_code;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-5 py-4 flex items-center gap-4 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/5 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold text-xl">Merchant QR</h1>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto space-y-6">
        {/* Business Info */}
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-3">
            <Store className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-heading font-bold text-xl text-white">{merchantData?.business_name}</h2>
          <p className="text-sm text-muted-foreground">Merchant ID: {merchantData?.merchant_id?.slice(-8)}</p>
        </div>

        {/* QR Code Display */}
        <div className="glass rounded-2xl p-6">
          <div className="bg-white rounded-xl p-4 mb-4">
            <QRCodeSVG
              id="merchant-qr-code"
              value={qrValue}
              size={220}
              level="H"
              includeMargin
              className="w-full h-auto"
              fgColor="#0f172a"
              bgColor="#ffffff"
            />
          </div>
          
          {showAmountQR && requestAmount && (
            <div className="text-center mb-4 py-2 px-4 bg-primary/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Amount Requested</p>
              <p className="text-2xl font-bold text-primary">J${parseFloat(requestAmount).toLocaleString()}</p>
            </div>
          )}

          <p className="text-center text-sm text-muted-foreground mb-4">
            Customers can scan this QR code to pay you instantly
          </p>

          <div className="grid grid-cols-3 gap-2">
            <Button
              onClick={() => copyToClipboard(qrValue)}
              variant="outline"
              className="bg-white/5 border-white/10"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
            <Button
              onClick={downloadQR}
              variant="outline"
              className="bg-white/5 border-white/10"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              onClick={shareQR}
              variant="outline"
              className="bg-white/5 border-white/10"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Request Specific Amount */}
        <div className="glass rounded-xl p-4">
          <h3 className="font-medium text-white mb-3">Request Specific Amount</h3>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">J$</span>
              <Input
                type="number"
                placeholder="0.00"
                className="input-glass pl-10"
                value={requestAmount}
                onChange={(e) => {
                  setRequestAmount(e.target.value);
                  setShowAmountQR(false);
                }}
              />
            </div>
            <Button onClick={generatePaymentQR} className="btn-primary">
              Generate
            </Button>
          </div>
          {showAmountQR && (
            <button
              onClick={() => {
                setShowAmountQR(false);
                setRequestAmount("");
              }}
              className="text-xs text-primary mt-2"
            >
              Clear amount
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="glass rounded-xl p-4 text-center">
            <DollarSign className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
            <p className="text-lg font-bold text-white">
              J${(merchantData?.total_received || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Total Received</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <Clock className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <p className="text-lg font-bold text-white">{merchantData?.transactions_today || 0}</p>
            <p className="text-xs text-muted-foreground">Today</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <TrendingUp className="w-5 h-5 text-amber-400 mx-auto mb-2" />
            <p className="text-lg font-bold text-white">
              J${(merchantData?.average_transaction || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Average</p>
          </div>
        </div>

        {/* Tips */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-medium text-white mb-3">Tips for Merchants</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>Print and display your QR code at your checkout counter</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>Use specific amount QR codes for fixed-price items</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>Payments are instant - no waiting for card processing</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>Lower fees than traditional card payments</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default MerchantQR;
